/**
 * Custom Backend adapter for the commerce library
 * This adapter connects to a custom backend with multiple microservices built on top of Shopify
 */

import { ICustomConfig } from "./interface";
import {
  IProductQueryReqest,
  ISingleProductRequest,
  IOptionsRequest,
  IResponse,
  ICartItemRequest,
  ICartUpdateRequest,
  ISingleCollectionRequest,
  ICheckoutUpdateRequest,
  IPaymentRequest,
  ICustomerQueryRequest,
  ICustomerRequest,
  IOrderQueryRequest,
  IOrderRequest,
} from "../../models";
import { ProductService } from "./product/service";
import { OrderService } from "./order/service";
import { CartService } from "./cart/service";
import { BaseCommerceClient } from "../../core/client";

/**
 * Custom Backend client implementation
 * This client uses microservices for different domains (product, customer, user, etc.)
 */
export class CustomBackendClient extends BaseCommerceClient {
  protected readonly adapterName = "Custom Backend";
  private productService!: ProductService;
  private orderService!: OrderService;
  private cartService!: CartService;

  constructor(config: ICustomConfig) {
    super();

    // Initialize product service
    this.productService = new ProductService(
      config.services.product.apiBaseUrl,
      config
    );

    // Initialize order service with customer API URL (since orders are customer-related data)
    this.orderService = new OrderService(
      config.services.order.apiBaseUrl,
      config
    );

    // Initialize cart service
    this.cartService = new CartService(config.services.cart.apiBaseUrl, config);
  }

  // ========================================
  // PRODUCT OPERATIONS
  // ========================================

  // Product operations - Enhanced for PDP support
  async getProduct(
    params: ISingleProductRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getProduct(params, options);
  }

  async getProducts(
    params: IProductQueryReqest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getProducts(params, options);
  }

  // ========================================
  // COLLECTION OPERATIONS
  // ========================================

  getCollection(
    params: ISingleCollectionRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.productService.getCollection(params, options);
  }

  // ========================================
  // CART OPERATIONS
  // ========================================

  async getCart(id: string, options?: IOptionsRequest): Promise<IResponse> {
    return this.cartService.getCart(id, options);
  }

  // Cart operations - For "Add to Cart" functionality
  async createCart(options?: IOptionsRequest): Promise<IResponse> {
    return this.cartService.createCart(options);
  }

  async updateCart(
    id: string,
    data: ICartUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.updateCart(id, data, options);
  }

  async addToCart(
    cartId: string,
    items: ICartItemRequest[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.addToCart(cartId, items, options);
  }

  async removeFromCart(
    cartId: string,
    itemIds: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.removeFromCart(cartId, itemIds, options);
  }

  // ========================================
  // CHECKOUT OPERATIONS
  // ========================================

  // Checkout operations
  async createCheckout(
    payload: any,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.cartService.createCheckout(payload, options);
  }

  async updateCheckout(
    id: string,
    data: ICheckoutUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Checkout service not implemented in custom adapter");
  }

  async completeCheckout(
    id: string,
    paymentData: IPaymentRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Checkout service not implemented in custom adapter");
  }

  // ========================================
  // CUSTOMER OPERATIONS
  // ========================================

  // Customer operations
  async getCustomer(id: string, options?: IOptionsRequest): Promise<IResponse> {
    throw new Error("Customer service not implemented in custom adapter");
  }

  async getCustomers(
    params: ICustomerQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Customer service not implemented in custom adapter");
  }

  async createCustomer(
    data: ICustomerRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Customer service not implemented in custom adapter");
  }

  async updateCustomer(
    id: string,
    data: Partial<ICustomerRequest>,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Customer service not implemented in custom adapter");
  }

  // ========================================
  // ORDER OPERATIONS
  // ========================================

  async getOrder(id: string, options?: IOptionsRequest): Promise<IResponse> {
    throw new Error("Order service not implemented in custom adapter");
  }

  async getOrders(
    params: IOrderQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Order service not implemented in custom adapter");
  }

  async createOrder(
    data: IOrderRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Order service not implemented in custom adapter");
  }

  async updateOrder(
    id: string,
    data: Partial<IOrderRequest>,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    throw new Error("Order service not implemented in custom adapter");
  }

  async getOrderHistory(
    customerAccessToken: string,
    page = 1,
    limit = 10,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.orderService.getOrderHistory(
      customerAccessToken,
      page,
      limit,
      options
    );
  }

  async getOrderById(
    customerAccessToken: string,
    orderId: string,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    return this.orderService.getOrderById(
      customerAccessToken,
      orderId,
      options
    );
  }
}
